/**
 * FieldEditModal - 단일 필드 편집 전용 바텀시트 모달
 *
 * MeasureItemCard에서 각 필드를 개별 클릭하면 해당 필드만 편집하는 콤팩트 모달
 * - text/number/select/textarea 타입 지원
 * - 바텀시트 스타일 애니메이션
 * - 필드 라벨 + 단위 헤더 표시
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { FieldConfig } from "@ict/shared";

interface FieldEditModalProps {
  open: boolean;
  onClose: () => void;
  field: FieldConfig;
  value: unknown;
  onSave: (value: unknown) => void;
  isSubmitting?: boolean;
  /** text 필드를 textarea로 렌더링할지 여부 */
  inputType?: "input" | "textarea";
}

export function FieldEditModal({
  open,
  onClose,
  field,
  value,
  onSave,
  isSubmitting = false,
  inputType = "input",
}: FieldEditModalProps) {
  // 애니메이션을 위한 내부 상태
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const rafIdRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // 로컬 편집 값
  const [localValue, setLocalValue] = useState<string>("");

  // 모달 열릴 때 값 초기화
  useEffect(() => {
    if (open) {
      const initialStr =
        value === null || value === undefined
          ? ""
          : value === 0 && field.type === "number"
            ? ""
            : String(value);
      setLocalValue(initialStr);
    }
  }, [open, value, field.type]);

  // ESC 키 핸들러
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  // 뒤로가기 이벤트 핸들러 (Android 백버튼 / 헤더 뒤로가기)
  const handleCloseTopModal = useCallback(
    (e: Event) => {
      if (!isSubmitting) {
        e.preventDefault();
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  useEffect(() => {
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    if (open) {
      // 스크롤 잠금 (overflow: hidden만 사용하여 fixed 포지셔닝 간섭 방지)
      document.body.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";

      setIsVisible(true);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = requestAnimationFrame(() => {
          setIsAnimating(true);
          // 모달 열린 후 input에 포커스
          setTimeout(() => inputRef.current?.focus(), 100);
        });
      });
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("closeTopModal", handleCloseTopModal);
    } else {
      // 스크롤 잠금 해제
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";

      setIsAnimating(false);
      closeTimer = setTimeout(() => setIsVisible(false), 200);
    }

    return () => {
      // cleanup 시에도 스크롤 잠금 해제
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      if (closeTimer) clearTimeout(closeTimer);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("closeTopModal", handleCloseTopModal);
    };
  }, [open, handleKeyDown, handleCloseTopModal]);

  if (!isVisible) return null;

  const handleBackdropClick = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSave = () => {
    let finalValue: unknown = localValue;
    if (field.type === "number") {
      finalValue = parseFloat(localValue) || 0;
    }
    onSave(finalValue);
  };

  // 헤더 라벨: 필드명 + 단위
  const headerLabel = field.unit ? `${field.label} (${field.unit})` : field.label;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${field.label} 편집`}
      className={`fixed inset-0 z-50 flex flex-col justify-end transition-colors duration-200 ${
        isAnimating ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={handleBackdropClick}
    >
      {/* 키보드 높이만큼 하단 여백을 줘서 바텀시트가 키보드 위에 위치 */}
      <div
        className={`w-full rounded-t-2xl bg-white overflow-hidden transition-transform duration-200 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ marginBottom: "var(--keyboard-height, 0px)" }}
        onClick={handleContentClick}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-[16px] font-semibold text-gray-900">{headerLabel}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 active:bg-gray-100 transition-colors disabled:opacity-50"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 입력 영역 */}
        <div className="px-4 py-4">
          {field.type === "select" && field.options ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-[14px] text-gray-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors appearance-none"
              style={{ minHeight: 44 }}
            >
              <option value="" disabled>
                {field.label} 선택
              </option>
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : inputType === "textarea" ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              placeholder={field.placeholder ?? `${field.label}을(를) 입력하세요`}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-[14px] text-gray-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              rows={5}
              style={{ minHeight: 120 }}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              inputMode={field.type === "number" ? "decimal" : "text"}
              pattern={field.type === "number" ? "[0-9]*\\.?[0-9]*" : undefined}
              value={localValue}
              onChange={(e) => {
                if (field.type === "number") {
                  // 숫자, 소수점, 마이너스만 허용
                  const v = e.target.value;
                  if (v === "" || v === "-" || /^-?\d*\.?\d*$/.test(v)) {
                    setLocalValue(v);
                  }
                } else {
                  setLocalValue(e.target.value);
                }
              }}
              placeholder={field.placeholder ?? `${field.label}을(를) 입력하세요`}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-[14px] text-gray-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
              style={{ minHeight: 44 }}
            />
          )}

          {/* 버튼 영역 */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-xl border border-gray-200 bg-white py-3 text-[14px] font-medium text-gray-600 active:bg-gray-50 transition-colors disabled:opacity-50"
              style={{ minHeight: 44 }}
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex-1 rounded-xl bg-[#3182F6] py-3 text-[14px] font-semibold text-white active:bg-[#2272E6] transition-colors disabled:opacity-50"
              style={{ minHeight: 44 }}
            >
              {isSubmitting ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
